import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";
import { api } from "@/lib/api";
import { fmtSignedUsd, fmtUsd, fmtPct, colorForPnl } from "@/lib/format";

export default function Analytics() {
  const { data: stats } = useQuery({
    queryKey: ["stats"],
    queryFn: api.stats,
    refetchInterval: 15000,
  });

  const equity = stats?.equity_curve || [];
  const byExArr = Object.entries(stats?.by_exchange || {}).map(([ex, v]) => ({
    exchange: ex,
    net_pnl: v.net_pnl,
    bonus: v.bonus,
    count: v.count,
  }));

  return (
    <div className="p-5 space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KPI label="Toplam İşlem" value={stats?.closed_positions ?? 0} testId="stat-trades" />
        <KPI
          label="Kazanma Oranı"
          value={fmtPct(stats?.win_rate, 1)}
          testId="stat-winrate"
          color={(stats?.win_rate || 0) >= 50 ? "text-[#2bd98a]" : "text-[#ff5662]"}
        />
        <KPI
          label="En İyi İşlem"
          value={fmtSignedUsd(stats?.best_trade)}
          color="text-[#2bd98a]"
          testId="stat-best"
        />
        <KPI
          label="En Kötü İşlem"
          value={fmtSignedUsd(stats?.worst_trade)}
          color="text-[#ff5662]"
          testId="stat-worst"
        />
        <KPI
          label="Brüt K/Z"
          value={fmtSignedUsd(stats?.total_gross_pnl)}
          color={colorForPnl(stats?.total_gross_pnl)}
          testId="stat-gross"
        />
        <KPI
          label="Kazanılan Bonus"
          value={fmtUsd(stats?.total_bonus_credit)}
          color="text-[#ff5d15]"
          testId="stat-bonus-total"
        />
        <KPI
          label="Ödenen Ücretler"
          value={fmtUsd(stats?.total_fees)}
          color="text-zinc-400"
          testId="stat-fees-total"
        />
        <KPI
          label="Net Gerçekleşen"
          value={fmtSignedUsd(stats?.total_net_pnl)}
          color={colorForPnl(stats?.total_net_pnl)}
          testId="stat-net-total"
        />
      </div>

      {/* Equity curve */}
      <Panel title="Sermaye Eğrisi · Net Gerçekleşen K/Z">
        {equity.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={equity} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="equityFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0A84FF" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#0A84FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1A1A1A" />
              <XAxis
                dataKey="t"
                stroke="#71717A"
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                tickFormatter={(t) => new Date(t).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })}
              />
              <YAxis
                stroke="#71717A"
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                tickFormatter={(v) => `$${v.toFixed(0)}`}
              />
              <Tooltip
                contentStyle={{
                  background: "#121212",
                  border: "1px solid #27272A",
                  borderRadius: 4,
                  fontFamily: "JetBrains Mono",
                  fontSize: 12,
                }}
                labelFormatter={(t) => new Date(t).toLocaleString("tr-TR")}
                formatter={(v, n) => [`$${Number(v).toFixed(2)}`, n === "net" ? "Birikimli" : "İşlem"]}
              />
              <Area
                type="monotone"
                dataKey="net"
                stroke="#0A84FF"
                strokeWidth={2}
                fill="url(#equityFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Empty>Henüz kapatılmış işlem yok — sermaye eğrisi için en az bir hedge çiftini kapat.</Empty>
        )}
      </Panel>

      {/* By exchange */}
      <Panel title="Karşı Borsaya Göre Performans">
        {byExArr.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byExArr} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#1A1A1A" />
              <XAxis dataKey="exchange" stroke="#71717A" tick={{ fontSize: 11 }} />
              <YAxis
                stroke="#71717A"
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                tickFormatter={(v) => `$${v.toFixed(0)}`}
              />
              <Tooltip
                contentStyle={{
                  background: "#121212",
                  border: "1px solid #27272A",
                  borderRadius: 4,
                  fontFamily: "JetBrains Mono",
                  fontSize: 12,
                }}
                formatter={(v) => `$${Number(v).toFixed(2)}`}
              />
              <Bar dataKey="net_pnl" name="Net K/Z" fill="#0A84FF" radius={[2, 2, 0, 0]} />
              <Bar dataKey="bonus" name="Bonus" fill="#ff5d15" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Empty>Henüz veri yok.</Empty>
        )}
      </Panel>
    </div>
  );
}

const KPI = ({ label, value, color = "text-white", testId }) => (
  <div data-testid={testId} className="tx-card" style={{ padding: "12px 14px" }}>
    <span className="tx-cb3" />
    <span className="tx-cb4" />
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`text-xl font-mono font-semibold mt-0.5 ${color}`}>{value}</div>
  </div>
);

const Panel = ({ title, children }) => (
  <section className="tx-card">
    <span className="tx-cb3" />
    <span className="tx-cb4" />
    <div className="tx-ch-head">
      <div className="tx-ch-tt">
        <h2 style={{ fontSize: 13, textTransform: "uppercase",
                     letterSpacing: "0.18em", color: "#a1a8b3" }}>{title}</h2>
      </div>
    </div>
    <div className="tx-card-b" style={{ padding: "14px 16px" }}>{children}</div>
  </section>
);

const Empty = ({ children }) => (
  <div className="h-[180px] flex items-center justify-center text-sm text-zinc-500 text-center px-6">
    {children}
  </div>
);
